import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { DeviceType, WidgetType } from '@/utils/definitions';
import { ChangelogTooltip } from '@/features/editor/widgets/changelog/changelog-tooltip';
import { ChangeTarget } from '@/features/editor/widgets/changelog/change-target-button';
import {
  Controls,
  Icon,
  Ordinal,
  Title,
  Wrapper,
} from '@/features/editor/widgets/changelog/shared/shared';
import { useChangelogTile } from './hooks/use-changelog-tile';
import { PlayAutomation } from './components/play-automation';
import { VisibilityToggle } from './components/visibility-toggle';
import { TitleFactory } from './title-renderers/title-factory';
import { SelectorTooltipWrapper } from '@/features/editor/widgets/changelog/components';

export interface ChangelogTileProps {
  ordinal: number;
  changeType: WidgetType;
  change: EditorDeclarativeBlock;

  device: DeviceType;

  onClick?: () => void;
  onHover?: () => void;
}

export function ChangelogTile({
  ordinal,
  device,
  onClick,
  onHover,
  change,
}: ChangelogTileProps) {
  const {
    isSynced,
    screenshot,
    onBeforeClick,
    onAutomationPlay,
    onToggleVisibility,
    shouldShowVisibilityToggle,
    appsCatalog,
  } = useChangelogTile({ change, device, onClick });

  return (
    <Wrapper
      onMouseEnter={onHover}
      onClick={onBeforeClick}
      device={device}
      isSynced={isSynced}
    >
      <SelectorTooltipWrapper />
      {change?.block?.kind === `automation` ? (
        <PlayAutomation device={device} onClick={onAutomationPlay} />
      ) : (
        <Ordinal device={device}>{ordinal}</Ordinal>
      )}
      <Title device={device}>
        <TitleFactory
          change={change}
          ordinal={ordinal}
          appsCatalog={appsCatalog}
          screenshot={screenshot}
        />
        {` `}
        <ChangeTarget isSynced={isSynced} change={change} />
      </Title>
      <Controls>
        <VisibilityToggle
          change={change}
          isSynced={shouldShowVisibilityToggle}
          onClick={onToggleVisibility}
        />
        <Icon data-tip data-for={change.editorId}>
          <StaticImage
            height={20}
            placeholder="none"
            loading="eager"
            src="../../../../assets/three_dots_2.svg"
            alt="close"
          />
        </Icon>
      </Controls>
      <ChangelogTooltip change={change} isSynced={isSynced} />
    </Wrapper>
  );
}
